import { resolveResultStatus } from './result-status.models';

export const PASTE_FIELDS = [
  'ignore', 'position', 'bib', 'name', 'firstName', 'lastName', 'category', 'categoryRank', 'club', 'dog', 'breed', 'time'
] as const;
export type PasteField = typeof PASTE_FIELDS[number];
export interface PastedTable { headers: string[]; mapping: PasteField[]; rows: string[][]; }
const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
const aliases: Record<string, PasteField> = {
  rang: 'position', place: 'position', classement: 'position', position: 'position', rank: 'position', pl: 'position', scratch: 'position',
  dossard: 'bib', bib: 'bib', dos: 'bib', doss: 'bib',
  nomprenom: 'name', prenomnom: 'name', nomcomplet: 'name', name: 'name', coureur: 'name', concurrent: 'name', participant: 'name', athlete: 'name',
  nom: 'lastName', lastname: 'lastName', prenom: 'firstName', firstname: 'firstName',
  categorie: 'category', category: 'category', cat: 'category', rangcategorie: 'categoryRank', plcat: 'categoryRank',
  club: 'club', equipe: 'club', association: 'club', chien: 'dog', nomduchien: 'dog', dog: 'dog', nomchien: 'dog',
  racechien: 'breed', racedechien: 'breed', breed: 'breed', temps: 'time', chrono: 'time', time: 'time', tempsfinal: 'time', tempstotal: 'time', tempsreel: 'time'
};

/** Quoted TSV/CSV, preserving empty cells and line breaks inside a quoted cell. */
export function delimitedRows(text: string): string[][] {
  const normalized = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
  const first = normalized.split('\n').find(line => line.trim()) || '';
  const delimiter = first.includes('\t') ? '\t' : first.includes(';') ? ';' : first.includes(',') ? ',' : null;
  if (!delimiter) return normalized.split('\n').filter(line => line.trim()).map(line => line.trim().split(/ {2,}/));
  const rows: string[][] = []; let row: string[] = []; let cell = ''; let quoted = false;
  for (let index = 0; index < normalized.length; index++) {
    const char = normalized[index];
    if (char === '"') {
      if (quoted && normalized[index + 1] === '"') { cell += '"'; index++; }
      else if (quoted || !cell) quoted = !quoted;
      else cell += char;
    } else if (!quoted && (char === delimiter || char === '\n')) {
      row.push(cell.trim()); cell = '';
      if (char === '\n') { if (row.some(value => value)) rows.push(row); row = []; }
    } else cell += char;
  }
  if (quoted) throw new Error('quotes');
  row.push(cell.trim()); if (row.some(value => value)) rows.push(row);
  return rows;
}

/** Only text is extracted; pasted markup is never inserted into the live page. */
export function clipboardRows(text: string, html = ''): string[][] {
  if (html) {
    const document = new DOMParser().parseFromString(html, 'text/html');
    const table = document.querySelector('table');
    if (table) {
      const rows = Array.from(table.rows).map(row => Array.from(row.cells).flatMap(cell => {
        const value = (cell.textContent || '').replace(/\s+/g, ' ').trim();
        return [value, ...Array(Math.min(cell.colSpan, 30) - 1).fill('')];
      })).filter(row => row.some(value => value));
      if (rows.length) return rows;
    }
  }
  return delimitedRows(text);
}

export function preparePastedTable(rows: string[][], hasHeader: boolean): PastedTable {
  const width = Math.max(0, ...rows.map(row => row.length));
  if (!rows.length || width > 40 || rows.length > 5001) throw new Error('size');
  const headers = hasHeader ? [...rows[0], ...Array(width - rows[0].length).fill('')] : Array.from({ length: width }, (_, index) => String(index + 1));
  const seen = new Set<PasteField>();
  const mapping = headers.map(header => {
    const field = hasHeader ? aliases[normalize(header)] || 'ignore' : 'ignore';
    if (field !== 'ignore' && seen.has(field)) return 'ignore';
    seen.add(field); return field;
  });
  return { headers, mapping, rows: rows.slice(hasHeader ? 1 : 0).map(row => Array.from({ length: width }, (_, index) => row[index] || '')) };
}

export function looksLikeHeader(row: string[]): boolean {
  return row.filter(cell => !!aliases[normalize(cell)]).length >= 2;
}

export function parsePastedTime(value: string): { seconds: number; status: string | null } | null {
  const status = resolveResultStatus({ rawTime: value });
  if (status) return { seconds: 0, status: status.code };
  const text = value.trim().replace(',', '.');
  // Clock notation is required to avoid treating an Excel day fraction or a rank as a time.
  const match = text.match(/^(?:(\d{1,3}):)?(\d{1,3}):(\d{2})(?:\.(\d{1,3}))?$/);
  if (!match) return null;
  const hours = Number(match[1] || 0), minutes = Number(match[2]), seconds = Number(match[3]);
  if (seconds >= 60 || (match[1] && minutes >= 60)) return null;
  const total = hours * 3600 + minutes * 60 + seconds + Number(`0.${match[4] || 0}`);
  return total > 0 && total < 86400 ? { seconds: total, status: null } : null;
}

export interface PastedResult {
  name: string; dog: string; breed: string; club: string; category: string;
  position: number | null; categoryRank: number | null; bib: string;
  seconds: number; status: string | null; rawTime: string;
}

export function validatePastedTable(table: PastedTable): { results: PastedResult[]; errors: { row: number; key: string }[] } {
  const errors: { row: number; key: string }[] = [];
  const results: PastedResult[] = [];
  const fields = table.mapping.filter(field => field !== 'ignore');
  if (new Set(fields).size !== fields.length) errors.push({ row: 0, key: 'duplicateColumns' });
  if (!fields.includes('name') && !fields.includes('lastName')) errors.push({ row: 0, key: 'nameColumn' });
  const hasTime = fields.includes('time');
  const seen = new Set<string>();
  table.rows.forEach((cells, index) => {
    const row = index + 1;
    const value = (field: PasteField) => (cells[table.mapping.indexOf(field)] || '').trim();
    const name = value('name') || [value('firstName'), value('lastName')].filter(Boolean).join(' ');
    const rawTime = value('time');
    // A copied ranking may only contain the finishing order. Preserve these
    // rows as untimed results instead of blocking the complete import.
    const time = hasTime && rawTime ? parsePastedTime(rawTime) : { seconds: 0, status: 'not_timed' };
    if (!name) errors.push({ row, key: 'missingName' });
    if (hasTime && rawTime && !time) errors.push({ row, key: 'invalidTime' });
    const integer = (field: PasteField) => {
      const text = value(field);
      if (!text || (field === 'position' && resolveResultStatus({ rawTime: text }))) return null;
      if (!/^\d+$/.test(text) || Number(text) < 1 || Number(text) > 2147483647) { errors.push({ row, key: 'invalidRank' }); return null; }
      return Number(text);
    };
    const position = integer('position'), categoryRank = integer('categoryRank');
    if (cells.some(cell => cell.length > 300)) errors.push({ row, key: 'longCell' });
    const identity = `${normalize(name)}|${normalize(value('dog'))}`;
    if (name && seen.has(identity)) errors.push({ row, key: 'duplicateRow' });
    seen.add(identity);
    results.push({ name, dog: value('dog'), breed: value('breed'), club: value('club'), category: value('category'),
      position: time?.status && time.status !== 'not_timed' ? null : position,
      categoryRank: time?.status && time.status !== 'not_timed' ? null : categoryRank, bib: value('bib'),
      seconds: time?.seconds ?? 0, status: time?.status ?? null, rawTime });
  });
  return { results, errors };
}
